import { REGISTRATION_PROFILE_JSON_KEY, parseProfileJurisdiction } from '@buxus/shared'
import {
  ABP_AUTH_TOKEN_STORAGE,
  ABP_USER_ID_STORAGE,
  GET_REGISTRATION_PROFILE_URL,
  PROFILE_JURISDICTION_STORAGE_KEY,
  PROFILE_STAKEHOLDER_STORAGE_KEY,
} from '../abp/config'
import { endOidcAndServerSession } from './authSessionTeardown'
import {
  clearLoginStartOidcClaim,
  consumePendingLoginAnalytics,
} from '../analytics/loginAnalytics'
import { registerAnalyticsStakeholderType, trackEvent } from '../analytics/webAnalytics'
import {
  AppEventTypes,
  outcomeFailureProps,
  outcomeSuccessProps,
} from '@buxus/shared'
import {
  LOGIN_FORCE_PROMPT_STORAGE_KEY,
  LOGIN_PROFILE_ERROR_STORAGE_KEY,
  PORTAL_ONLY_ACCOUNT_ERROR_CODE,
  PORTAL_ONLY_ACCOUNT_LOGIN_ERROR,
  REGISTRATION_PROFILE_LOGIN_ERROR,
} from './loginErrorMessage'
import { oidcManager } from './oidcManager'
import { reportReliabilityFailure } from '../errors/webFailureReporting'

export {
  AUTH_STORAGE_PURGED_KEY,
  callAccountLogout,
  endOidcAndServerSession,
  purgeClientAuthStorage,
} from './authSessionTeardown'
export type { SessionTeardownHints } from './authSessionTeardown'
export { oidcManager } from './oidcManager'
export const PROFILE_SYNCED_ON_CALLBACK_KEY = 'buxus-profile-synced-on-oidc-callback-v1'

function stableUserIdFromOidcProfile(profile: Record<string, unknown> | undefined): string {
  const sub = typeof profile?.sub === 'string' ? profile.sub.trim() : ''
  if (sub) return sub
  const preferred =
    typeof profile?.preferred_username === 'string' ? profile.preferred_username.trim() : ''
  if (preferred) return preferred
  const email = typeof profile?.email === 'string' ? profile.email.trim() : ''
  if (email) return email
  return 'oidc-user'
}

/** Copy a non-expired OIDC access token into `ABP_AUTH_TOKEN_STORAGE` for API calls. */
export async function syncAccessTokenFromOidcStore(): Promise<boolean> {
  const user = await oidcManager.getUser().catch(() => null)
  if (!user?.access_token?.trim() || user.expired) return false

  localStorage.setItem(ABP_AUTH_TOKEN_STORAGE, user.access_token.trim())
  return true
}

function storeLoginProfileError(message?: string): void {
  try {
    sessionStorage.setItem(
      LOGIN_PROFILE_ERROR_STORAGE_KEY,
      message?.trim() || REGISTRATION_PROFILE_LOGIN_ERROR,
    )
  } catch {
    /* ignore private mode */
  }
}

function readAbpRemoteError(body: unknown): { code?: string; message?: string } {
  if (!body || typeof body !== 'object') return {}
  const error = (body as { error?: unknown }).error
  if (!error || typeof error !== 'object') return {}
  const e = error as { code?: unknown; message?: unknown }
  return {
    code: typeof e.code === 'string' ? e.code : undefined,
    message: typeof e.message === 'string' ? e.message.trim() : undefined,
  }
}

export type ProfileSyncResult = {
  ok: boolean
  /** Prefer server-localized message when profile sync fails for a known business case. */
  loginErrorMessage?: string
  errorCode?: string
}

export async function beginOidcLogin(opts?: { loginHint?: string }): Promise<void> {
  const extraQueryParams: Record<string, string> = {}
  if (opts?.loginHint) extraQueryParams.login_hint = opts.loginHint
  const envForcePrompt =
    (import.meta.env.VITE_OIDC_PROMPT_LOGIN as string | undefined)?.trim() === 'true'
  let forcePrompt = envForcePrompt
  try {
    if (!forcePrompt && sessionStorage.getItem(LOGIN_FORCE_PROMPT_STORAGE_KEY) === '1') {
      forcePrompt = true
      sessionStorage.removeItem(LOGIN_FORCE_PROMPT_STORAGE_KEY)
    }
  } catch {
    /* ignore */
  }
  if (forcePrompt) extraQueryParams.prompt = 'login'
  await oidcManager.signinRedirect({ extraQueryParams })
}

function coerceStakeholderTypeString(v: unknown): string | null {
  if (typeof v === 'string' && v.trim().length > 0) return v.trim()
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return null
}

function readStakeholderTypeFromProfilePayload(json: unknown): string | null {
  const root = json && typeof json === 'object' ? (json as Record<string, unknown>) : null
  if (!root) return null
  const data = root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root

  const direct = coerceStakeholderTypeString(data.stakeholderType)
  if (direct) return direct

  const reg =
    data.registration && typeof data.registration === 'object'
      ? (data.registration as Record<string, unknown>)
      : null
  if (reg) {
    const fromReg = coerceStakeholderTypeString(reg.stakeholderType)
    if (fromReg) return fromReg
  }
  return null
}

/** Re-fetch registration profile + stakeholder/jurisdiction caches. Required for a valid session. */
export async function syncProfileStakeholderFromRegistrationApi(): Promise<ProfileSyncResult> {
  const token = localStorage.getItem(ABP_AUTH_TOKEN_STORAGE)
  if (!token?.trim()) return { ok: false }
  return fetchAndStoreProfileStakeholder(token.trim(), null)
}

async function fetchAndStoreProfileStakeholder(
  accessToken: string,
  idToken: string | null,
): Promise<ProfileSyncResult> {
  try {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    }
    if (idToken) headers['X-ID-Token'] = idToken

    const res = await fetch(GET_REGISTRATION_PROFILE_URL, {
      method: 'GET',
      headers,
      credentials: 'omit',
    })
    if (!res.ok) {
      const body = await res.json().catch(() => null)
      const { code, message } = readAbpRemoteError(body)
      if (code === PORTAL_ONLY_ACCOUNT_ERROR_CODE) {
        return {
          ok: false,
          errorCode: code,
          loginErrorMessage: message || PORTAL_ONLY_ACCOUNT_LOGIN_ERROR,
        }
      }
      reportReliabilityFailure(
        new Error(message || `Registration profile unavailable (HTTP ${res.status})`),
        {
          context: 'fetchRegistrationProfile',
          failureKind: 'http',
          metadata: {
            profile: { httpStatus: res.status, errorCode: code ?? null },
          },
          analyticsEvent: AppEventTypes.NETWORK_REQUEST_FAILED,
          analyticsProps: {
            httpStatus: res.status,
            ...(code != null ? { errorCode: code } : {}),
          },
        },
      )
      return { ok: false, errorCode: code }
    }
    const json = await res.json().catch(() => null)
    try {
      const root = json && typeof json === 'object' ? (json as Record<string, unknown>) : null
      const data =
        root?.data && typeof root.data === 'object'
          ? (root.data as Record<string, unknown>)
          : root
      if (data && Object.keys(data).length > 0) {
        localStorage.setItem(REGISTRATION_PROFILE_JSON_KEY, JSON.stringify(data))
      }
    } catch {
      /* ignore quota / private mode */
    }
    const stakeholderType = readStakeholderTypeFromProfilePayload(json)
    if (stakeholderType) {
      localStorage.setItem(PROFILE_STAKEHOLDER_STORAGE_KEY, stakeholderType)
    }
    const jur = parseProfileJurisdiction(json)
    if (jur.kind === 'restricted' && jur.keys.size > 0) {
      localStorage.setItem(PROFILE_JURISDICTION_STORAGE_KEY, JSON.stringify([...jur.keys]))
    } else if (jur.kind === 'unrestricted') {
      try {
        localStorage.removeItem(PROFILE_JURISDICTION_STORAGE_KEY)
      } catch {
        /* ignore */
      }
    }
    return { ok: true }
  } catch (error) {
    reportReliabilityFailure(error, {
      context: 'fetchRegistrationProfile',
      metadata: {
        profile: { phase: 'network' },
      },
    })
    return { ok: false }
  }
}

export async function maybeHandleOidcCallback(): Promise<boolean> {
  const url = new URL(window.location.href)
  const isCallbackPath = url.pathname.endsWith('/auth/callback')
  const hasCode = url.searchParams.has('code')
  const hasState = url.searchParams.has('state')
  if (!isCallbackPath && !(hasCode && hasState)) return false

  clearLoginStartOidcClaim()

  const user = await oidcManager.signinRedirectCallback()
  const accessToken = user?.access_token?.trim()
  const pendingLogin = consumePendingLoginAnalytics()
  const loginEmail =
    pendingLogin?.email ||
    (typeof user?.profile?.email === 'string' ? user.profile.email.trim() : '') ||
    (typeof user?.profile?.preferred_username === 'string'
      ? user.profile.preferred_username.trim()
      : '')
  const loginMode = pendingLogin?.mode ?? 'user'

  if (!accessToken) {
    trackEvent(AppEventTypes.USER_LOGIN_FLOW_FAILED, {
      email: loginEmail,
      mode: loginMode,
      ...outcomeFailureProps({
        errorMessage: 'No access token received',
        failureKind: 'unknown',
      }),
    })
    storeLoginProfileError()
    window.history.replaceState({}, document.title, window.location.origin + '/')
    await endOidcAndServerSession({ idToken: user?.id_token ?? null })
    return false
  }

  const profileResult = await fetchAndStoreProfileStakeholder(accessToken, user?.id_token ?? null)
  if (!profileResult.ok) {
    trackEvent(AppEventTypes.USER_LOGIN_FLOW_FAILED, {
      email: loginEmail,
      mode: loginMode,
      errorCode: profileResult.errorCode,
      ...outcomeFailureProps({
        errorMessage:
          profileResult.errorCode === PORTAL_ONLY_ACCOUNT_ERROR_CODE
            ? 'Portal only account'
            : 'Registration profile unavailable',
        failureKind:
          profileResult.errorCode === PORTAL_ONLY_ACCOUNT_ERROR_CODE ? 'validation' : 'http',
      }),
    })
    storeLoginProfileError(profileResult.loginErrorMessage)
    window.history.replaceState({}, document.title, window.location.origin + '/')
    await endOidcAndServerSession({
      accessToken,
      idToken: user?.id_token ?? null,
    })
    return false
  }

  try {
    sessionStorage.removeItem(LOGIN_FORCE_PROMPT_STORAGE_KEY)
  } catch {
    /* ignore */
  }

  localStorage.setItem(ABP_AUTH_TOKEN_STORAGE, accessToken)
  localStorage.setItem(
    ABP_USER_ID_STORAGE,
    stableUserIdFromOidcProfile(user?.profile as Record<string, unknown> | undefined),
  )

  try {
    sessionStorage.setItem(PROFILE_SYNCED_ON_CALLBACK_KEY, '1')
  } catch {
    /* ignore */
  }

  registerAnalyticsStakeholderType(localStorage.getItem(PROFILE_STAKEHOLDER_STORAGE_KEY))

  trackEvent(AppEventTypes.USER_LOGGED_IN, {
    email: loginEmail,
    mode: loginMode,
    ...outcomeSuccessProps(),
  })
  trackEvent(AppEventTypes.USER_DATA_SAVED, {
    email: loginEmail,
    ...outcomeSuccessProps(),
  })

  // Clean URL after callback.
  window.history.replaceState({}, document.title, window.location.origin + '/')
  return true
}

/** @deprecated Prefer `endOidcAndServerSession` from the adapter logout path. */
export async function beginOidcLogout(): Promise<void> {
  await endOidcAndServerSession({
    accessToken: localStorage.getItem(ABP_AUTH_TOKEN_STORAGE),
    idToken: (await oidcManager.getUser().catch(() => null))?.id_token ?? null,
  })
}

